import { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Search as SearchIcon } from "lucide-react";
import { ZONES } from "@/utils/constants";
import { trackHubEvent } from "@/utils/hub";

function SearchDropdown({ options, query, activeIdx, searching, onPick, onClose, instance }) {
  let lastGroup = null;
  return (
    <div className="absolute top-full mt-2 left-0 w-80 max-w-[calc(100vw-2rem)] bg-white border-2 border-navy/15 shadow-[8px_8px_0_rgba(17,41,107,0.12)] z-50 overflow-hidden pb-2" data-testid={`search-autocomplete-${instance}`}>
      {options.length === 0 && searching && (
        <div className="px-4 py-3" data-testid={`search-loading-${instance}`}>
          <p className="text-sm font-body text-gray-500 animate-pulse">Searching…</p>
        </div>
      )}
      {options.length === 0 && !searching && (
        <div className="px-4 py-3" data-testid={`search-no-results-${instance}`}>
          <p className="text-sm font-body text-gray-600">No matches found</p>
          <Link to={`/wellness-hub?q=${encodeURIComponent(query)}`} onClick={onClose}
            className="block text-sm font-body font-semibold text-brand-red hover:text-red-800 mt-1.5" data-testid={`search-hub-fallback-${instance}`}>
            Search the Wellness Hub for &ldquo;{query}&rdquo;
          </Link>
        </div>
      )}
      {options.map((opt, i) => {
        const showGroup = opt.group !== lastGroup;
        lastGroup = opt.group;
        return (
          <div key={opt.id} className="px-3">
            {showGroup && <p className="text-gray-500 text-xs font-body uppercase tracking-wider mt-2 mb-1">{opt.group}</p>}
            <button
              onClick={() => onPick(opt)}
              aria-current={activeIdx === i || undefined}
              className={`w-full flex items-center justify-between text-left text-sm font-body px-2 py-2 transition-colors ${activeIdx === i ? "bg-slate-100 text-navy" : "text-gray-600 hover:text-navy hover:bg-slate-50"}`}
              data-testid={opt.testId ? `${opt.testId}-${instance}` : undefined}
            >
              <span className="truncate">{opt.label}</span>
              {opt.meta && <span className="text-xs text-gray-500 flex-shrink-0 ml-2 capitalize">{opt.meta}</span>}
            </button>
          </div>
        );
      })}
      {options.length > 0 && (
        <Link to={`/wellness-hub?q=${encodeURIComponent(query)}`} onClick={onClose}
          className="block px-5 py-2.5 mt-1 border-t border-slate-100 text-sm font-body font-semibold text-brand-red hover:bg-slate-50" data-testid={`search-hub-fallback-${instance}`}>
          Search the Wellness Hub for &ldquo;{query}&rdquo;
        </Link>
      )}
    </div>
  );
}

function useSearchOptions(query) {
  const [remote, setRemote] = useState([]);
  const [hubItems, setHubItems] = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (query.length < 2) { setRemote([]); setHubItems([]); setRecovery(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      Promise.allSettled([
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/listings`, { params: { page: 1, limit: 4, q: query } })
          .then((r) => setRemote(r.data?.items || [])),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/hub/content`, { params: { limit: 3, q: query } })
          .then((r) => setHubItems(r.data?.items || [])),
        axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/search/recovery`, { params: { q: query } })
          .then((r) => setRecovery(r.data || null)),
      ]).finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const options = useMemo(() => {
    if (query.length < 2) return [];
    const opts = [];
    if (recovery?.zipSuggestion) opts.push({ id: `nearby-${recovery.zipSuggestion.zip}`, group: "Nearby", label: `Search within 25 miles of ${recovery.zipSuggestion.zip}`, to: `/?zip=${recovery.zipSuggestion.zip}&radius=25` });
    ZONES.filter((z) => z.toLowerCase().includes(query.toLowerCase())).slice(0, 3)
      .forEach((z) => opts.push({ id: `zone-${z}`, group: "Zones", label: z, zone: z }));
    remote.forEach((l) => opts.push({ id: `venue-${l.id}`, group: "Venues", label: l.name, meta: l.zone, to: `/listing/${l.id}` }));
    const existingVenueIds = new Set(remote.map((listing) => listing.id));
    recovery?.venues?.filter((venue) => !existingVenueIds.has(venue.id)).slice(0, 3)
      .forEach((venue) => opts.push({ id: `similar-venue-${venue.id}`, group: "Similar venues", label: venue.name, meta: venue.city, to: `/listing/${venue.id}` }));
    recovery?.areas?.filter((area) => !ZONES.includes(area.name)).slice(0, 2)
      .forEach((area) => opts.push({ id: `similar-area-${area.slug || area.name}`, group: "Similar areas", label: area.name, zone: area.name }));
    hubItems.forEach((h) => opts.push({ id: `hub-${h.id || h.slug}`, group: "Wellness Hub", label: h.title, meta: h.type, to: `/wellness-hub/${h.slug}`, testId: `search-hub-${h.slug}` }));
    return opts;
  }, [query, remote, hubItems, recovery]);

  const optionsRef = useRef([]);
  useEffect(() => { optionsRef.current = options; }, [options]);
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return;
    const t = setTimeout(() => trackHubEvent("search", null, { q, source: "header", results: optionsRef.current.length }), 1500);
    return () => clearTimeout(t);
  }, [query]);

  return { options, searching };
}

function useHeaderSearchController(onSearch, searchQuery) {
  const [focused, setFocused] = useState(false);
  const [query, setQuery] = useState(searchQuery || "");
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { options, searching } = useSearchOptions(query);

  useEffect(() => { setQuery(searchQuery || ""); }, [searchQuery]);
  useEffect(() => { setActiveIdx(-1); }, [query]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setFocused(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const pick = useCallback((opt) => {
    if (!opt) return;
    if (opt.to) { navigate(opt.to); }
    else if (opt.zone) { setQuery(opt.zone); onSearch?.(opt.zone); }
    setFocused(false);
  }, [navigate, onSearch]);

  const submit = useCallback(() => {
    const directoryQuery = query.trim();
    if (!directoryQuery) return;
    onSearch?.(directoryQuery);
    navigate(`/?q=${encodeURIComponent(directoryQuery)}`);
    setFocused(false);
  }, [navigate, onSearch, query]);

  const onKeyDown = useCallback((e) => {
    if (e.key === "Escape") { setFocused(false); e.currentTarget.blur(); return; }
    if (query.length < 2) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setFocused(true); setActiveIdx((i) => Math.min(i + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActiveIdx((i) => Math.max(i - 1, -1)); }
    else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIdx >= 0 && options[activeIdx]) pick(options[activeIdx]);
      else submit();
    }
  }, [activeIdx, options, pick, query, submit]);

  const clear = useCallback(() => { setQuery(""); onSearch?.(""); }, [onSearch]);
  return { activeIdx, clear, focused, onKeyDown, options, pick, query, ref, searching, setFocused, setQuery, submit };
}

export function HeaderSearch({ onSearch, searchQuery, instance = "default" }) {
  const controller = useHeaderSearchController(onSearch, searchQuery);

  return (
    <form ref={controller.ref} onSubmit={(event) => { event.preventDefault(); controller.submit(); }} className="relative flex-1 min-w-0 max-w-md" role="search">
      <input
        type="text"
        role="combobox"
        aria-expanded={controller.focused && controller.query.length >= 2}
        aria-label="Search venues, zones, and articles"
        placeholder="Search venue, city, or topic"
        value={controller.query}
        onChange={(e) => { controller.setQuery(e.target.value); onSearch?.(e.target.value); }}
        onFocus={() => controller.setFocused(true)}
        onKeyDown={controller.onKeyDown}
        className="bg-slate-50 text-navy placeholder-gray-400 pl-4 pr-20 py-2 rounded-none text-sm font-body border border-navy/15 focus:outline-none focus:border-navy focus:bg-white w-full min-h-[44px] transition-colors"
        data-testid={`header-search-input-${instance}`}
      />
      {controller.query.length > 0 && (
        <button
          type="button"
          onClick={controller.clear}
          aria-label="Clear search"
          data-testid={`header-search-clear-${instance}`}
          className="absolute right-11 top-1/2 -translate-y-1/2 text-gray-500 hover:text-navy z-10 px-1 py-1 text-xs font-body font-semibold"
        >
          Clear
        </button>
      )}
      <button type="submit" disabled={!controller.query.trim()} aria-label="Submit venue search" className="absolute right-1 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center bg-brand-red text-white transition-[background-color,opacity] hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-40" data-testid={`header-search-submit-${instance}`}><SearchIcon className="h-4 w-4" /></button>
      {controller.focused && controller.query.length >= 2 && (
        <SearchDropdown options={controller.options} query={controller.query} activeIdx={controller.activeIdx} searching={controller.searching} onPick={controller.pick} onClose={() => controller.setFocused(false)} instance={instance} />
      )}
    </form>
  );
}
